import {
  AppBar,
  Menu,
  MenuItem,
  Box,
  Toolbar,
  IconButton,
  Typography,
  Container,
  styled,
} from '@mui/material';
import { useDesignSchema } from '@Src/providers/DesignSchemaProvider';
import { ReactComponent as HamburgerMenuIcon } from '@Src/assets/images/elixir/hamburger-menu.svg';
import { useTranslation } from '@Src/providers/TranslationProvider';
import { Link } from '@Components/RoutesUtils';
import { Suspense, lazy, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useIsMobile } from '@Src/hooks/useIsMobile';
import { V2PageType } from '@Src/middlewares/CustomPageTypes';
import { useFeatureFlag } from '@Src/providers/FeatureFlagsProvider';
import { useConfig } from '@Src/providers/ConfigProvider';

const LoginDialog = lazy(
  async () => await import('@Components/WebsiteV2/LogIn/LoginDialog'),
);

const StyledAppBar = styled(AppBar)`
  position: relative !important;
  ${({ theme }) => theme.breakpoints.down('sm')} {
    height: 56px;
    .desktop-menu {
      display: none;
      visibility: hidden;
    }
  }
  ${({ theme }) => theme.breakpoints.up('sm')} {
    height: 88px;
    .mobile-menu {
      display: none;
      visibility: hidden;
    }
  }
  justify-content: center;
  background-color: ${(props) =>
    props.theme.palette.background.default} !important;
  .MuiPopover-paper {
    display: flex;
    justify-content: center;
    align-items: center;
  }
`;

const StyledLogo = styled('img')`
  ${({ theme }) => theme.breakpoints.down('sm')} {
    height: 36px;
    width: 36px;
  }
  ${({ theme }) => theme.breakpoints.up('sm')} {
    height: 48px;
    width: 48px;
  }
  border-radius: 20%;
`;

const StyledBurgerMenuIcon = styled(HamburgerMenuIcon)`
  position: absolute;
  z-index: 10;
  color: ${(props) =>
    props.theme.palette.getContrastText(
      props.theme.palette.background.default,
    )};
`;

const StyledMenu = styled(Menu)`
  [role='menu'] {
    padding-top: 0;
    padding-bottom: 0;
    box-shadow: none !important;
    background-color: ${(props) =>
      props.theme.palette.background.default} !important;
  }
`;

export const StyledLogoLink = styled(Link)`
  justify-content: left;
  overflow: visible;
  display: flex;
`;

const StyledMenuText = styled(Typography)`
  color: ${(props) =>
    props.theme.palette.getContrastText(
      props.theme.palette.background.default,
    )};
  text-align: center;
  text-transform: capitalize;
`;

type StyledMenuLinkProps = {
  isCurrent: boolean;
};

const StyledMenuItem = styled(MenuItem, {
  shouldForwardProp: (prop) => prop !== 'isCurrent',
})<StyledMenuLinkProps>`
  &:hover,
  &:focus {
    background-color: transparent !important;
    a {
      h5 {
        color: ${(props) => props.theme.palette.action.active} !important;
      }
    }
  }
  min-height: 48px !important;
  ${({ theme }) => theme.breakpoints.down('sm')} {
    border-bottom: ${(props) =>
      `1px solid ${props.theme.palette.getContrastText(
        props.theme.palette.background.default,
      )} !important`};
  }
  border-bottom: none !important;
  a {
    text-decoration: none !important;
    h5 {
      color: ${(props) =>
        props.isCurrent
          ? props.theme.palette.action.active
          : props.theme.palette.getContrastText(
              props.theme.palette.background.default,
            )} !important;
    }
  }
`;

const StyledIconButton = styled(IconButton)`
  width: 30px;
  height: 30px;
`;

const Header = (): JSX.Element => {
  const { logo, navigationMenuLinks } = useDesignSchema();
  const { translate } = useTranslation();
  const enableLogin = useFeatureFlag('enableLogin');
  const location = useLocation();
  const ref = useRef(null);
  const { isLoggedIn } = useConfig();

  const isMobile = useIsMobile();
  const [open, setOpen] = useState(false);
  const [isLoginOpen, setIsLoginOpen] = useState<boolean>(false);

  const menuItems = navigationMenuLinks
    ?.filter((link) => link.pageEnabled && link.displayIn === 'header')
    ?.sort((a, b) => (a.order || 0) - (b.order || 0))
    ?.map((link) => {
      const isCurrent =
        location.pathname === link.url ||
        (link.pageType === V2PageType.LandingPage && location.pathname === '/');

      return (
        <StyledMenuItem
          data-fd={link?.title || ''}
          isCurrent={isCurrent}
          key={link?.url}
          onClick={() => setOpen(false)}
        >
          <Link
            data-fd={(link?.title || '') + '-link' || ''}
            to={link?.url || '/'}
          >
            <StyledMenuText variant="body1xs">
              {translate(link?.title || '')}
            </StyledMenuText>
          </Link>
        </StyledMenuItem>
      );
    });

  if (enableLogin && !isLoggedIn) {
    menuItems?.push(
      <StyledMenuItem
        key="login"
        isCurrent={false}
        className="header-text"
        onClick={() => {
          setIsLoginOpen(true);
          setOpen(false);
        }}
      >
        <StyledMenuText variant="body1xs">{translate('Login')}</StyledMenuText>
      </StyledMenuItem>,
    );
  }

  useEffect(() => {
    // edge case in which screen is resized to mobile size
    open && setOpen(false);
  }, [location.pathname, isMobile]);

  return (
    <StyledAppBar ref={ref} elevation={0} position="absolute">
      <Container maxWidth={false}>
        <Toolbar disableGutters>
          <Box sx={{ flexGrow: 1 }}>
            <StyledLogoLink data-fd="logo" to="/">
              <StyledLogo alt={translate('Our_logo')} src={logo} />
            </StyledLogoLink>
          </Box>
          {
            <Box
              data-fd="header-desktop-menu"
              className="desktop-menu"
              sx={{ flexGrow: 0, overflow: 'auto', display: { xs: 'flex' } }}
            >
              {menuItems}
            </Box>
          }
          {
            <Box className="mobile-menu" sx={{ flexGrow: 0 }}>
              <StyledIconButton
                data-fd="header-mobile-button"
                aria-label={translate('Burger_Menu_Label')}
                aria-haspopup="true"
                onClick={() => setOpen(true)}
              >
                <StyledBurgerMenuIcon />
              </StyledIconButton>
              <StyledMenu
                autoFocus={false}
                id="header-mobile-menu"
                data-fd="header-mobile-menu"
                marginThreshold={0}
                slotProps={{
                  root: {
                    style: {
                      paddingTop: 0,
                      paddingBottom: 0,
                    },
                  },
                  paper: {
                    style: {
                      paddingTop: 0,
                      paddingBottom: 0,
                      width: '100%',
                      maxWidth: '90%',
                      borderRadius: 0,
                      boxShadow: '0px 5px 5px -5px rgba(0, 0, 0, 0.2)',
                    },
                  },
                }}
                anchorEl={ref.current}
                anchorOrigin={{
                  vertical: 'bottom',
                  horizontal: 'center',
                }}
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'center',
                }}
                open={open}
                onClose={() => setOpen(false)}
              >
                {menuItems}
              </StyledMenu>
            </Box>
          }
        </Toolbar>
      </Container>
      {enableLogin && isLoginOpen && (
        <Suspense fallback={<div>{translate('loading')}</div>}>
          <LoginDialog onClose={() => setIsLoginOpen(false)} />
        </Suspense>
      )}
    </StyledAppBar>
  );
};
export default Header;
